/* eslint-disable react-hooks/exhaustive-deps */
import { MainContent } from '@/components/content'
import { AutocompleteAddress } from '@/components/input'
import { MainLayout } from '@/components/layouts'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { OFFSET } from '@/constants/Common'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { ROUTE_PLAY } from '@/constants/Routes'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppSelector } from '@/hooks/useRedux'

import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { getPlaceDetail, useGetClubs, useGetMyClubs, useQueryGetEvents, useQueryGetMyEvents } from '@/services'
import { Club, Event, EventStatus } from '@/types'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ClubContent } from '../components/ClubContent'
import { EventContent } from '../components/EventContent'
import { Filter } from '../components/Filter'
import { Tab } from '../components/Tab'

export const Play = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const { isShowDropdown } = useAppSelector(appSelector)
  const { user } = useAppSelector(userSelector)

  const [isShowFilter, setIsShowFilter] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [offset, setOffset] = useState(OFFSET)
  const [isNearMe, setIsNearMe] = useState(false)
  const [tabActive, setTabActive] = useState<'CLUBS' | 'EVENTS'>(location.state?.goBackEvent ? 'EVENTS' : 'CLUBS')
  const [eventStatus, setEventStatus] = useState<EventStatus[]>(['UPCOMING', 'IN_PROGRESS'])
  const [locationData, setLocationData] = useState<AutocompleteAddress>({ description: '', placeId: '' })
  const [geometry, setGeometry] = useState<{ lat: number; lng: number } | null>(null)
  const [distance, setDistance] = useState(0)
  const [hasFilter, setHasFilter] = useState(false)
  const [lastElementClub, setLastElementClub] = useState(null)
  const [lastElementEvent, setLastElementEvent] = useState(null)
  const [clubs, setClubs] = useState<Map<number, Club>>(new Map())
  const [events, setEvents] = useState<Map<number, Event>>(new Map())

  const debouncedSearchText = useDebounce<string>(searchText || '*', 200)
  const debouncedDistance = useDebounce<number>(distance || 0, 200)

  const { data: resClubs, isFetching: isFetchingClubs } = useGetClubs(
    {
      limit: 18,
      offset: offset,
      query: debouncedSearchText ? debouncedSearchText : '*',
      ...((geometry || isNearMe) && {
        location: {
          lat: geometry ? geometry?.lat : isNearMe ? (user?.addresses?.[0]?.latitude as number) : null,
          lng: geometry ? geometry?.lng : isNearMe ? (user?.addresses?.[0]?.longitude as number) : null,
          ...(debouncedDistance &&
            (geometry || isNearMe) && {
              radiusInMeters: debouncedDistance * 1609.34,
            }),
        },
      }),
    },
    tabActive === 'CLUBS'
  )
  const { data: resMyClubs, isFetching: isFetchingMyClubs } = useGetMyClubs(tabActive === 'CLUBS')
  const { data: resEvents, isFetching: isFetchingEvents } = useQueryGetEvents(
    {
      filters: {
        durationStatus: eventStatus,
      },
      lat: geometry ? geometry?.lat : isNearMe ? (user?.addresses?.[0]?.latitude as number) : null,
      lng: geometry ? geometry?.lng : isNearMe ? (user?.addresses?.[0]?.longitude as number) : null,
      limit: 10,
      isNearMe: isNearMe,
      offset: offset,
      query: debouncedSearchText ? debouncedSearchText : '*',
      radiusInMeters: debouncedDistance ? debouncedDistance * 1609.34 : null,
    },
    tabActive === 'EVENTS'
  )
  const { data: resMyEvents, isFetching: isFetchingMyEvents } = useQueryGetMyEvents(
    {
      limit: 2,
      offset: OFFSET,
    },
    tabActive === 'EVENTS'
  )

  const observerClub = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + 18)
      }
    })
  )

  const observerEvent = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + 10)
      }
    })
  )

  const onChangeTab = (tab: 'CLUBS' | 'EVENTS') => {
    setOffset(OFFSET)
    setSearchText('')
    setGeometry(null)
    setDistance(0)
    setLocationData({ description: '', placeId: '' })
    setIsShowFilter(false)
    setHasFilter(false)
    setIsNearMe(false)
    if (tab !== tabActive) setTabActive(tab)
    if (location.state?.goBackEvent) navigate(ROUTE_PLAY)
  }

  const toggleFilter = () => setIsShowFilter(!isShowFilter)

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
    setOffset(OFFSET)
  }

  const onChangeSwitch = (checked: boolean) => {
    setIsNearMe(checked)
    setOffset(OFFSET)
  }

  const checkArrayContent = () => {
    if (eventStatus.length !== 2) {
      return false
    }

    const set = new Set(eventStatus)
    return set.has('UPCOMING') && set.has('IN_PROGRESS') && set.size === 2
  }

  const onChangeLocation = (data: AutocompleteAddress) => {
    setLocationData(data)
    setOffset(OFFSET)
  }

  const onFilterEventStatus = (type: EventStatus) => {
    setOffset(OFFSET)
    if (eventStatus.includes(type))
      setEventStatus((pre) => {
        return [...pre].filter((item) => item !== type)
      })

    if (!eventStatus.includes(type)) setEventStatus([...eventStatus, type])
  }

  const onGetPlaceDetail = async (address: AutocompleteAddress) => {
    try {
      const res = await getPlaceDetail(address?.placeId)
      setGeometry({ lat: res.result.geometry.location.lat, lng: res.result.geometry.location.lng })
      setOffset(OFFSET)
    } catch (error) {
      toast.error(APP_CUSTOM_ERROR)
    }
  }

  const onChangeDistance = (e: number) => {
    setDistance(e)
    setOffset(OFFSET)
  }

  const onClearAll = () => {
    setHasFilter(false)
    setOffset(OFFSET)
    setLocationData({ description: '', placeId: '' })
    setGeometry(null)
    setDistance(0)
    setEventStatus(['UPCOMING', 'IN_PROGRESS'])
  }

  useEffect(() => {
    if (resClubs && tabActive === 'CLUBS') {
      setClubs((old) => {
        const temp = offset === OFFSET ? new Map() : new Map(old)
        resClubs.result.hits.forEach((ele) => {
          temp.set(ele.clubId, ele)
        })
        return temp
      })
    }
  }, [resClubs, tabActive])

  useEffect(() => {
    if (resEvents && tabActive === 'EVENTS') {
      setEvents((old) => {
        const temp = offset === OFFSET ? new Map() : new Map(old)
        resEvents.result.hits.forEach((ele) => {
          temp.set(ele.leagueId, ele)
        })
        return temp
      })
    }
  }, [resEvents, tabActive])

  useEffect(() => {
    const currentElement = lastElementClub
    const currentObserver = observerClub.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElementClub])

  useEffect(() => {
    const currentElement = lastElementEvent
    const currentObserver = observerEvent.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElementEvent])

  useEffect(() => {
    if ((locationData.description && locationData.placeId) || distance || !checkArrayContent()) {
      setHasFilter(true)
    } else {
      setHasFilter(false)
    }
  }, [locationData, distance, eventStatus])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent isShowFilter={isShowFilter}>
          <div className="flex flex-col mb-20 lg:mb-0 pb-8">
            <div className="flex flex-col">
              <h1 className="text-2xl sm:text-[32px] sm:leading-10 font-bold text-white">{t('common.play')}</h1>
              <Tab
                tabActive={tabActive}
                onChangeTab={(tab) => onChangeTab(tab)}
                searchText={searchText}
                onChangeInput={(e) => onChangeInput(e)}
                toggleFilter={toggleFilter}
                isNearMe={isNearMe}
                onChangeSwitch={(checked) => onChangeSwitch(checked)}
                hasFilter={hasFilter}
              />
            </div>

            {tabActive === 'CLUBS' && (
              <ClubContent
                isLoadingClubs={isFetchingClubs}
                isLoadingMyClubs={isFetchingMyClubs}
                myClubs={resMyClubs?.results}
                clubs={clubs}
                hasMore={resClubs?.result?.hasMore}
                setLastElement={setLastElementClub}
              />
            )}

            {tabActive === 'EVENTS' && (
              <EventContent
                isLoadingEvents={isFetchingEvents}
                isLoadingMyEvents={isFetchingMyEvents}
                myEvents={resMyEvents?.result?.hits}
                events={events}
                hasMore={resEvents?.result?.hasMore}
                setLastElement={setLastElementEvent}
                isMyEventEmpty={resMyEvents?.result?.empty}
              />
            )}

            {/* Filter */}
            {isShowFilter && (
              <Filter
                toggleFilter={toggleFilter}
                onClearAll={onClearAll}
                eventStatus={eventStatus}
                onFilterEventStatus={(type) => onFilterEventStatus(type)}
                location={locationData}
                onChangeLocation={onChangeLocation}
                setGeometry={setGeometry}
                onGetPlaceDetail={(address) => onGetPlaceDetail(address)}
                distance={distance}
                onChangeDistance={onChangeDistance}
                tabActive={tabActive}
              />
            )}
          </div>
        </MainContent>
      </MainLayout>
    </>
  )
}
